import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../environment";

const BASE_URL = `${API_BASE_PATH}`;

// ================= AUTH HEADERS =================
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  return {
    Authorization: token,
    Accept: "application/json",
  };
};

// ================= COMMON ERROR HANDLER =================
const handleApiError = (error: any, fallbackMsg: string) => {
  const errorMessage =
    error?.response?.data?.details ||
    error?.response?.data?.message ||
    fallbackMsg;
  toast.error(errorMessage);
};

// ✅ User Interface
export interface User {
  id?: string;
  name: string;
  email: string;
  phone_no: string;
  role: string;
  status: boolean;
  organisation_id: string;
  client_id?: string;
  client_name?: string;
  password?: string;
  photo?: File | null;
  is_deleted?: boolean;
  last_activity?: string;
  created_at?: string;
}

// ✅ User Profile Interface
export interface UserProfile {
  id: string;
  email: string;
  phone_no: string;
  name: string;
  role: string;
  status: boolean;
  client_id: string;
  client_name: string;
  First_name: string;
  Last_name: string;
  Address: string;
  Country: string;
  State_Provience: string;
  City?: string | null;
  postal_code?: string | null;
}

// ✅ Create New User
export const createUser = async (user: User, token?: string): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("name", user.name);
    formData.append("email", user.email);
    formData.append("password", user.password || "");
    formData.append("phone_no", user.phone_no);
    formData.append("role", user.role || "user");
    formData.append("status", String(user.status));
    formData.append("is_deleted", String(user.is_deleted ?? false));

    if (user.organisation_id) {
      formData.append("organisation_id", user.organisation_id);
    }

    if (user.client_id) {
      formData.append("client_id", user.client_id);
    }

    if (user.photo) {
      formData.append("photo", user.photo);
    }

    const headers: Record<string, string> = {
      accept: "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    await axios.post(`${BASE_URL}/create_user`, formData, { headers });
    toast.success("User created successfully");
  } catch (error) {
    console.error("Create User Error", error);
    toast.error("Error creating user");
    throw error;
  }
};

// ✅ Update User (Only Role and Optional Photo)
// ================= UPDATE USER (ALL FIELDS) =================
export const updateUser = async (
  userId: string,
  payload: Partial<{
    name: string;
    user_name: string;
    email: string;
    role: string;
    status: boolean;
    phone_no: string;
    first_name: string;
    last_name: string;
    address: string;
    city: string;
    state_province: string;
    country: string;
    postal_code: string;
    lock_until?: string;
    lock_action?: string;
    photo: File | null;
  }>
): Promise<boolean> => {
  try {
    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      // Skip lock_until if lock_action is not "add"
      if (key === "lock_until" && payload.lock_action !== "add") {
        return;
      }

      if (typeof value === "boolean") {
        formData.append(key, String(value));
      } else if (value instanceof File) {
        formData.append(key, value);
      } else {
        formData.append(key, value as string);
      }
    });

    await axios.put(`${BASE_URL}/users/${userId}`, formData, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });

    toast.success("User updated successfully");
    return true;
  } catch (error: any) {
    handleApiError(error, "Failed to update user");
    return false;
  }
};

// ✅ Update Full User Profile + Photo
export const updateUserProfileWithPhoto = async (userId: string, data: Partial<UserProfile> & { photo?: File | null }): Promise<void> => {
  try {
    const formData = new FormData();

    if (data.First_name) formData.append("First_name", data.First_name);
    if (data.Last_name) formData.append("Last_name", data.Last_name);
    if (data.Address) formData.append("Address", data.Address);
    if (data.Country) formData.append("Country", data.Country);
    if (data.State_Provience) formData.append("State_Provience", data.State_Provience);
    if (data.City) formData.append("City", data.City);
    if (data.postal_code) formData.append("postal_code", data.postal_code);
    if (data.role) formData.append("role", data.role);
    if (data.status !== undefined) formData.append("status", String(data.status));
    if (data.photo) formData.append("photo", data.photo);

    await axios.put(`${BASE_URL}/user/${userId}/photo`, formData, {
    headers: {
        ...getAuthHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });

    toast.success("User profile updated successfully!");
  } catch (error) {
    handleApiError(error, "Failed to update user");
    throw error;
  }
};

// ✅ Get All Users
export const fetchAllUsers = async (token: string): Promise<User[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/custome_response_for_get_two`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch users");
    throw error;
  }
};

// ✅ Get Single User by ID
export const fetchUser = async (userId: string, token: string): Promise<User> => {
  try {
    const response = await axios.get(`${BASE_URL}/user/${userId}`, {
      headers: { Authorization: token },
    });
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch user");
    throw error;
  }
};

// ✅ Get User Profile by ID
// ================= GET USER PROFILE =================
export const fetchUserProfileById = async (
  userId: string
): Promise<UserProfile | null> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/user/profile/manage/user/${userId}`,
      { headers: getAuthHeaders() }
    );

    return res.data || null;
  } catch (error: any) {
    handleApiError(error, "Failed to fetch user profile");
    return null;
  }
};


// ✅ Delete User
export const deleteUser = async (userId: string, token?: string): Promise<void> => {
  try {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    await axios.delete(`${BASE_URL}/user/${userId}/delete`, { headers });
    toast.success("User deleted successfully");
  } catch (error) {
    toast.error("Error deleting user");
    throw error;
  }
};

// ✅ Get User Photo (Blob for download/display)
export const getUserPhoto = async (userId: string, token: string): Promise<Blob> => {
  try {
    const response = await axios.get(`${BASE_URL}/user/${userId}/photo`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch user photo");
    throw error;
  }
};

export interface PlanWidgetCounts {
  counts: {
    total: {
      total_plan: number;
    };
    "plan type": {
      total_plan_type: number;
    };
    active: {
      active_plan: number;
    };
    inactive: {
      inactive_plan: number;
    };
  };
}

export const fetchPlanWidgetCounts = async (token?: string): Promise<PlanWidgetCounts> => {
  try {
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.get(`${BASE_URL}/get_all_widgets_plans`, { headers });
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch widget plan data");
    throw error;
  }
};


export const forgotPassword = async (email: string): Promise<void> => {
  try {
    const formData = new URLSearchParams();
    formData.append("email", email);
 
    await axios.post(`${BASE_URL}/forgot-password`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
 
    toast.success("Reset link sent to your email!");
  } catch (error) {
    console.error("Forgot Password Error:", error);
    toast.error("Failed to send reset link.");
    throw error;
  }
};
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  try {
    const formData = new URLSearchParams();
    formData.append("token", token);
    formData.append("new_password", newPassword);
 
    await axios.post(`${BASE_URL}/reset-password`, formData, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });
 
    toast.success("Password reset successfully!");
  } catch (error) {
    console.error("Reset Password Error:", error);
    toast.error("Failed to reset password.");
    throw error;
  }
};
 
 